/**
 * Compound-unlock helpers — derives whether a compound word has been
 * "unlocked" by the user, based on the per-kanji progress map returned
 * from /api/progress.
 *
 * A compound is:
 *   - "unlocked"  when EVERY kanji constituent has status === 'known'
 *   - "partial"   when at least one kanji is 'known' or 'learning'
 *                 (but not all are 'known')
 *   - "locked"    otherwise (no relevant progress yet)
 *
 * Non-kanji characters (kana, punctuation) are ignored.
 */

export const UNLOCK_STATE = {
  UNLOCKED: "unlocked",
  PARTIAL:  "partial",
  LOCKED:   "locked",
};

/**
 * @param {string[]} kanjiList list of constituent characters of the compound
 * @param {Object} progressMap shape: { [kanji: string]: { status: 'new'|'learning'|'known' } }
 * @returns {{ state: string, known: number, total: number }}
 */
export function compoundUnlockState(kanjiList, progressMap) {
  const kanjiOnly = (kanjiList || []).filter((c) => /[\u4e00-\u9fff\u3400-\u4dbf]/.test(c));
  const total = kanjiOnly.length;
  if (!total) return { state: UNLOCK_STATE.LOCKED, known: 0, total: 0 };
  const map = progressMap || {};
  let known = 0;
  let touched = 0;
  for (const ch of kanjiOnly) {
    const s = map[ch]?.status;
    if (s === "known") { known += 1; touched += 1; }
    else if (s === "learning") { touched += 1; }
  }
  if (known === total) return { state: UNLOCK_STATE.UNLOCKED, known, total };
  if (touched > 0)     return { state: UNLOCK_STATE.PARTIAL,  known, total };
  return { state: UNLOCK_STATE.LOCKED, known, total };
}

export const UNLOCK_VISUAL = {
  unlocked: { label: "unlocked",     dot: "#6E7A5A", border: "#6E7A5A" }, // moss
  partial:  { label: "in progress",  dot: "#D29C4C", border: "#D29C4C" }, // amber
  locked:   { label: "locked",       dot: "#BDB6AB", border: "#BDB6AB" }, // muted
};
